import React, { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { DAY_TYPES, SPANISH_DOW, buildMonthMatrix, monthLabel, ymd } from "../lib/training";

/**
 * MonthCalendar - premium calendar UI
 * sessions: array of { id, date, day_type, title, match }
 * onDateClick(dateStr, sessionOrNull) - when admin/client taps a date
 * canAdd - admin only, shows + on empty days
 */
export default function MonthCalendar({ sessions = [], current, onChangeMonth, onDateClick, canAdd = false }) {
  const today = ymd(new Date());
  const grid = useMemo(
    () => buildMonthMatrix(current.getFullYear(), current.getMonth()),
    [current]
  );
  const byDate = useMemo(() => {
    const m = {};
    for (const s of sessions) m[s.date] = s;
    return m;
  }, [sessions]);

  const prev = () => {
    const d = new Date(current);
    d.setMonth(d.getMonth() - 1);
    onChangeMonth(d);
  };
  const next = () => {
    const d = new Date(current);
    d.setMonth(d.getMonth() + 1);
    onChangeMonth(d);
  };

  return (
    <div data-testid="month-calendar">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <button onClick={prev} className="qv-btn-ghost" style={{ fontSize: 11, padding: "8px 12px" }} data-testid="cal-prev">
          <ChevronLeft size={14} />
        </button>
        <div style={{ fontFamily: "Bebas Neue", fontSize: "1.6rem", letterSpacing: "0.04em" }}>
          {monthLabel(current).toUpperCase()}
        </div>
        <button onClick={next} className="qv-btn-ghost" style={{ fontSize: 11, padding: "8px 12px" }} data-testid="cal-next">
          <ChevronRight size={14} />
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, marginBottom: 6 }}>
        {SPANISH_DOW.map((d) => (
          <div key={d} style={{ textAlign: "center", fontSize: 10, color: "#737373", letterSpacing: "0.2em", fontWeight: 700, padding: "6px 0" }}>{d}</div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
        {grid.map((d, i) => {
          const ds = ymd(d);
          const inMonth = d.getMonth() === current.getMonth();
          const s = byDate[ds];
          const dt = s ? DAY_TYPES[s.day_type] : null;
          const isToday = ds === today;
          return (
            <button
              key={i}
              onClick={() => onDateClick && onDateClick(ds, s || null)}
              data-testid={`cal-cell-${ds}`}
              style={{
                position: "relative",
                aspectRatio: "1 / 1",
                minHeight: 56,
                background: dt ? dt.bg : (inMonth ? "#0a0a0a" : "#070707"),
                border: `1px solid ${dt ? dt.border : (isToday ? "rgba(225,29,46,0.7)" : "rgba(255,255,255,0.06)")}`,
                color: inMonth ? "#fff" : "#3a3a3a",
                cursor: "pointer",
                padding: 6,
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                justifyContent: "space-between",
                fontFamily: "Inter, sans-serif",
                fontSize: 13,
                transition: "transform .15s ease, border-color .15s ease",
              }}
              onMouseEnter={(e) => { if (inMonth) e.currentTarget.style.transform = "scale(1.04)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
            >
              <span style={{
                fontFamily: "Bebas Neue, sans-serif",
                fontSize: "1.05rem",
                color: isToday ? "var(--qv-red)" : (inMonth ? "#fff" : "#3a3a3a"),
                fontWeight: 700,
              }}>{d.getDate()}</span>
              {dt && (
                <>
                  {/* Secondary day-type icons (small dots top-right) */}
                  {(s.secondary_day_types || []).length > 0 && (
                    <div style={{ position: "absolute", top: 4, right: 4, display: "flex", gap: 2 }}>
                      {(s.secondary_day_types || []).slice(0, 3).map((sec, idx) => {
                        const sdt = DAY_TYPES[sec];
                        if (!sdt) return null;
                        const SIcon = sdt.icon;
                        return (
                          <div
                            key={idx}
                            title={sdt.label}
                            style={{
                              width: 14, height: 14,
                              background: sdt.bg,
                              border: `1px solid ${sdt.border}`,
                              display: "flex", alignItems: "center", justifyContent: "center",
                            }}
                          >
                            <SIcon size={8} color={sdt.color} />
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {dt.icon && (s.secondary_day_types || []).length === 0 && (
                    <dt.icon size={12} color={dt.color} style={{ alignSelf: "flex-end", opacity: 0.85 }} />
                  )}
                  <span style={{
                    position: "absolute",
                    bottom: 4,
                    left: 4,
                    right: 4,
                    fontSize: 9,
                    color: dt.color,
                    letterSpacing: "0.1em",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}>
                    {(s.day_type === "partido" || s.day_type === "partido_suplente") && s.match?.rival ? `vs ${s.match.rival}` : dt.label}
                  </span>
                </>
              )}
              {!dt && canAdd && inMonth && (
                <Plus size={12} color="#3a3a3a" style={{ alignSelf: "flex-end", opacity: 0.7 }} />
              )}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 18 }}>
        {Object.entries(DAY_TYPES).map(([k, v]) => (
          <div key={k} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#a3a3a3" }}>
            <span style={{ width: 10, height: 10, background: v.color, display: "inline-block" }} />
            {v.label}
          </div>
        ))}
      </div>
    </div>
  );
}
